import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, Users, Copy, MessageCircle, Mail, ArrowRight } from "lucide-react";
import { BrutalButton } from "@/components/Brutal";
import { useAuth } from "@/context/AuthContext";
import api, { API } from "@/lib/api";
import { toast } from "sonner";

function FamilyInvite() {
  const navigate = useNavigate();
  const { orgs, activeOrg } = useAuth();
  const org = activeOrg || localStorage.getItem("slackr_org") || "";
  const orgName = orgs?.find((o) => o.org_id === org)?.name || "je gezin";
  const joinPath = `/aanmelden${org ? `?org=${org}` : ""}`;
  const link = `${window.location.origin}${joinPath}`;
  const qr = `${API}/app-qr?base_url=${encodeURIComponent(window.location.origin)}&path=${encodeURIComponent(joinPath)}`;
  const waText = `Kom erbij op Slackr! Ik heb een Family Bundel geregeld, dus voor jou is het helemaal gratis. Meld je hier aan (${orgName} is al gekoppeld):\n${link}`;

  return (
    <div className="min-h-screen bg-brand-cream flex items-center justify-center p-6" data-testid="family-invite-step">
      <div className="brutal rounded-2xl bg-white p-7 max-w-sm w-full text-center space-y-4">
        <div className="brutal-sm rounded-full bg-brand-violet text-white w-14 h-14 flex items-center justify-center mx-auto"><Users className="w-7 h-7" strokeWidth={2.5} /></div>
        <div>
          <p className="font-head font-black text-2xl">Nodig je gezin uit! 🎉</p>
          <p className="text-sm text-brand-ink/60 font-semibold mt-1">Je Family Bundel is actief — tot 6 personen gratis. Vul de plekken meteen!</p>
        </div>
        <div className="brutal-sm rounded-xl bg-white p-2 inline-block"><img src={qr} alt="QR" className="w-40 h-40" data-testid="family-invite-qr" /></div>
        <div className="flex gap-2">
          <BrutalButton data-testid="family-invite-copy" variant="green" size="sm" className="flex-1" onClick={() => { navigator.clipboard?.writeText(link); toast.success("Link gekopieerd"); }}><Copy className="w-4 h-4" strokeWidth={2.5} />Link</BrutalButton>
          <BrutalButton data-testid="family-invite-wa" size="sm" className="flex-1" onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(waText)}`, "_blank")}><MessageCircle className="w-4 h-4" strokeWidth={2.5} />WhatsApp</BrutalButton>
          <BrutalButton data-testid="family-invite-email" variant="pink" size="sm" className="flex-1" onClick={() => window.open(`mailto:?subject=${encodeURIComponent("Kom bij " + orgName + " op Slackr")}&body=${encodeURIComponent(waText)}`)}><Mail className="w-4 h-4" strokeWidth={2.5} />E-mail</BrutalButton>
        </div>
        <BrutalButton data-testid="family-invite-manage" className="w-full" onClick={() => navigate("/abonnement")}>Leden toewijzen aan bundel<ArrowRight className="w-4 h-4" strokeWidth={2.5} /></BrutalButton>
        <button data-testid="family-invite-skip" onClick={() => navigate("/vandaag")} className="text-sm font-bold text-brand-ink/50 underline">Later, naar Slackr</button>
      </div>
    </div>
  );
}

export function PaymentSuccess() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { refresh } = useAuth();
  const [state, setState] = useState("checking"); // checking | paid | error
  const [plan, setPlan] = useState(null);
  const tries = useRef(0);

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) { setState("error"); return; }
    let stop = false;
    const poll = async () => {
      if (stop) return;
      tries.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") { setPlan(data.plan); setState("paid"); refresh(); return; }
        if (["failed", "expired"].includes(data.payment_status)) { setState("error"); return; }
      } catch { /* keep trying */ }
      if (tries.current >= 15) { setState("error"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
  }, [params, refresh]);

  if (state === "paid" && plan && String(plan).startsWith("family")) return <FamilyInvite />;

  return (
    <div className="min-h-screen bg-brand-cream flex items-center justify-center p-6" data-testid="payment-success">
      <div className="brutal rounded-2xl bg-white p-8 max-w-sm w-full text-center space-y-4">
        {state === "checking" && (<><Loader2 className="w-14 h-14 mx-auto animate-spin text-brand-violet" /><p className="font-head font-black text-xl">Betaling verwerken…</p><p className="text-sm text-brand-ink/60 font-semibold">Even geduld, we bevestigen je licentie.</p></>)}
        {state === "paid" && (<><CheckCircle2 className="w-14 h-14 mx-auto text-brand-green" data-testid="payment-paid" /><p className="font-head font-black text-2xl">Gelukt! 🎉</p><p className="text-sm text-brand-ink/60 font-semibold">Je licentie is geactiveerd. Veel plezier met Slackr!</p><BrutalButton className="w-full" onClick={() => navigate("/abonnement")}>Naar mijn abonnement</BrutalButton></>)}
        {state === "error" && (<><XCircle className="w-14 h-14 mx-auto text-brand-pink" /><p className="font-head font-black text-xl">Betaling niet bevestigd</p><p className="text-sm text-brand-ink/60 font-semibold">Is er iets misgegaan? Probeer het opnieuw of controleer later je status.</p><BrutalButton className="w-full" onClick={() => navigate("/abonnement")}>Terug naar abonnement</BrutalButton></>)}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-brand-cream flex items-center justify-center p-6" data-testid="payment-cancel">
      <div className="brutal rounded-2xl bg-white p-8 max-w-sm w-full text-center space-y-4">
        <XCircle className="w-14 h-14 mx-auto text-brand-ink/40" />
        <p className="font-head font-black text-xl">Betaling geannuleerd</p>
        <p className="text-sm text-brand-ink/60 font-semibold">Geen probleem — je kunt later altijd upgraden.</p>
        <BrutalButton className="w-full" onClick={() => navigate("/abonnement")}>Terug naar abonnement</BrutalButton>
      </div>
    </div>
  );
}
